import React, { useState, useEffect, useCallback } from 'react';
import { Settings, Plus, Trash2, AlertCircle, Edit3, Save, X } from 'lucide-react';
import taskTypeDefaultsService from '../../services/taskTypeDefaultsService';
import { useAuth } from '../../contexts/AuthContext';
import useNotification from '../../hooks/useNotification';
import Notification from '../common/Notification';
import ConfirmDialog from '../common/ConfirmDialog';

const TaskTypeDefaultsManager = () => {
  const { user } = useAuth();
  const { notification, showSuccess, showError: showErrorNotification, hideNotification } = useNotification();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [availableTypes, setAvailableTypes] = useState([]);
  const [defaults, setDefaults] = useState({ system: [], firm: [], user: [] });
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [defaultToDelete, setDefaultToDelete] = useState(null);
  const [formData, setFormData] = useState({
    task_type: '',
    default_duration_minutes: 60,
    default_duration_unit: 'minutes',
    buffer_before_minutes: 0,
    buffer_after_minutes: 15,
    is_moveable: true,
    level: 'user'
  });


  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const typesResponse = await taskTypeDefaultsService.getAvailableTypes();
      setAvailableTypes(typesResponse.data?.task_types || []);
      const defaultsResponse = await taskTypeDefaultsService.getTaskTypeDefaults();
      const defaultsData = defaultsResponse.data || {};
      
      if (Array.isArray(defaultsData)) {
        setDefaults({
          system: defaultsData.filter(d => !d.user_id && !d.firm_id),
          firm: defaultsData.filter(d => d.firm_id && !d.user_id),
          user: defaultsData.filter(d => d.user_id)
        });
      } else {
        setDefaults({
          system: defaultsData.system_defaults || [],
          firm: defaultsData.firm_defaults || [],
          user: defaultsData.user_defaults || []
        });
      }
    } catch (err) {
      console.error('Failed to fetch task type defaults:', err);
      setError('Failed to load task type defaults.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleInputChange = useCallback((field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  }, []);

  const convertToDisplayValue = useCallback((minutes, unit) => {
    return unit === 'hours' ? minutes / 60 : minutes;
  }, []);

  const convertFromDisplayValue = useCallback((value, unit) => {
    if (unit === 'hours') {
      return Math.min(Math.round((parseFloat(value) || 1.0) * 60), 1440);
    }
    return Math.min(parseInt(value) || 60, 1440);
  }, []);

  const handleAddDefaultClick = useCallback(() => {
    setShowCreateForm(true);
  }, []);

  const handleCreate = useCallback(async () => {
    try {
      if (!formData.task_type) {
        setError('Please select a task type');
        return;
      }

      // Check if user has a firm (required for creating user-level defaults)
      if (!user?.primary_firm_id) {
        setError('Your account is not associated with a firm. Please contact your administrator to set up your firm association before creating custom task defaults.');
        return;
      }

      const durationInMinutes = convertFromDisplayValue(
        formData.default_duration_minutes, 
        formData.default_duration_unit
      );

      const createData = {
        task_type: formData.task_type,
        default_duration_minutes: durationInMinutes,
        buffer_before_minutes: parseInt(formData.buffer_before_minutes),
        buffer_after_minutes: parseInt(formData.buffer_after_minutes),
        is_moveable: formData.is_moveable,
        firm_id: user.primary_firm_id,
        user_id: user.id
      };

      await taskTypeDefaultsService.createTaskTypeDefault(createData);
      await fetchData();
      setShowCreateForm(false);
      setFormData({
        task_type: '',
        default_duration_minutes: 60,
        default_duration_unit: 'minutes',
        buffer_before_minutes: 0,
        buffer_after_minutes: 15,
        is_moveable: true,
        level: 'user'
      });
      setError(null);
    } catch (err) {
      console.error('Failed to create task type default:', err);
      
      // Provide more specific error messages based on the response
      let errorMessage = 'Failed to create task type default.';
      
      if (err.response?.status === 409) {
        const taskTypeDisplay = formData.task_type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
        errorMessage = `You already have a personal default for "${taskTypeDisplay}". Please choose a different task type or edit the existing default.`;
      } else if (err.response?.status === 403) {
        errorMessage = 'You do not have permission to create task defaults. Please check your account permissions.';
      } else if (err.response?.status === 400) {
        errorMessage = err.response?.data?.detail || 'Invalid input. Please check your settings and try again.';
      } else if (err.response?.data?.detail) {
        errorMessage = err.response.data.detail;
      } else {
        errorMessage = 'Failed to create task type default. Please try again or contact support.';
      }
      
      setError(errorMessage);
    }
  }, [formData, user, fetchData, convertFromDisplayValue]);

  const handleUpdate = useCallback(async (defaultId, updateData) => {
    try {
      setError(null);
      await taskTypeDefaultsService.updateTaskTypeDefault(defaultId, updateData);
      await fetchData();
      setEditingId(null);
    } catch (err) {
      console.error('Failed to update task type default:', err);
      
      let errorMessage = 'Failed to update task type default.';
      if (err.response?.status === 403) {
        errorMessage = 'You do not have permission to update this default.';
      } else if (err.response?.status === 400) {
        errorMessage = err.response?.data?.detail || 'Invalid input. Please check your settings.';
      } else if (err.response?.data?.detail) {
        errorMessage = err.response.data.detail;
      }
      
      setError(errorMessage);
    }
  }, [fetchData]);

  const handleDelete = useCallback((defaultId) => {
    setDefaultToDelete(defaultId);
    setShowDeleteConfirm(true);
  }, []);

  const confirmDelete = useCallback(async () => {
    if (!defaultToDelete) return;

    try {
      setError(null);
      await taskTypeDefaultsService.deleteTaskTypeDefault(defaultToDelete);
      await fetchData();
      showSuccess('Task type default deleted successfully');
    } catch (err) {
      console.error('Failed to delete task type default:', err);

      let errorMessage = 'Failed to delete task type default.';
      if (err.response?.status === 403) {
        errorMessage = 'You do not have permission to delete this default.';
      } else if (err.response?.data?.detail) {
        errorMessage = err.response.data.detail;
      }

      showErrorNotification(errorMessage);
    } finally {
      setShowDeleteConfirm(false);
      setDefaultToDelete(null);
    }
  }, [defaultToDelete, fetchData, showSuccess, showErrorNotification]);

  const formatDuration = useCallback((minutes) => {
    const numMinutes = parseInt(minutes);
    if (numMinutes >= 60) {
      const hours = Math.floor(numMinutes / 60);
      const remainingMinutes = numMinutes % 60;
      if (remainingMinutes === 0) {
        return `${hours}h`;
      } else {
        return `${hours}h ${remainingMinutes}min`;
      }
    }
    return `${numMinutes}min`;
  }, []);

  const formatTaskType = useCallback((taskType) => {
    return taskType.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  }, []);

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-yellow-500"></div>
          <span className="ml-2">Loading task type defaults...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow">
      <div className="px-6 py-4 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <Settings className="h-5 w-5 text-gray-500 mr-2" />
            <h3 className="text-lg font-medium text-gray-900">Task Type Defaults</h3>
          </div>
          <button 
            onClick={handleAddDefaultClick}
            className="bg-yellow-500 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-yellow-600 flex items-center"
          >
            <Plus className="h-4 w-4 mr-1" />
            Add Default
          </button>
        </div>
        
        {error && (
          <div className="mt-4 bg-red-50 border border-red-200 rounded-md p-3 flex items-center">
            <AlertCircle className="h-4 w-4 text-red-600 mr-2" />
            <span className="text-sm text-red-700">{error}</span>
            <button onClick={() => setError(null)} className="ml-auto text-red-600 hover:text-red-800">
              <X className="h-4 w-4" />
            </button>
          </div>
        )}

        {!user?.primary_firm_id && (
          <div className="mt-4 bg-yellow-50 border border-yellow-200 rounded-md p-3 flex items-center">
            <AlertCircle className="h-4 w-4 text-yellow-600 mr-2" />
            <span className="text-sm text-yellow-700">
              Your account is not associated with a firm. You can view system defaults but cannot create custom ones. Please contact your administrator.
            </span>
          </div>
        )}
      </div>

      <div className="p-6">
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-6">
          <h4 className="text-sm font-medium text-gray-900 mb-2">About Task Type Defaults</h4>
          <p className="text-sm text-gray-800">
            Configure standard time estimates for different types of legal work. These defaults help with intelligent scheduling.
            {user?.primary_firm_id ? ' Personal defaults override firm defaults, which override system defaults.' : ' You are currently viewing system defaults only.'}
          </p>
        </div>

        {/* Create Form - Always show if button was clicked, regardless of firm */}
        {showCreateForm && (
          <div className="mb-6 bg-gray-50 border border-gray-200 rounded-lg p-4">
            <h4 className="text-md font-medium text-gray-900 mb-4">
              Create New Personal Default
              {!user?.primary_firm_id && <span className="text-red-600"> (Disabled - No Firm Association)</span>}
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Task Type</label>
                <select
                  value={formData.task_type}
                  onChange={(e) => handleInputChange('task_type', e.target.value)}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                  disabled={!user?.primary_firm_id}
                  required
                >
                  <option value="">Select Task Type</option>
                  {availableTypes.map(type => (
                    <option key={type} value={type}>{formatTaskType(type)}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Default Duration</label>
                <div className="flex space-x-2">
                  <input
                    type="number"
                    step={formData.default_duration_unit === 'hours' ? "0.25" : "15"}
                    min={formData.default_duration_unit === 'hours' ? "0.25" : "15"}
                    max={formData.default_duration_unit === 'hours' ? "24" : "1440"}
                    value={convertToDisplayValue(formData.default_duration_minutes, formData.default_duration_unit)}
                    onChange={(e) => {
                      const inputValue = formData.default_duration_unit === 'hours' 
                        ? Math.min(parseFloat(e.target.value) || 1.0, 24)
                        : Math.min(parseInt(e.target.value) || 60, 1440);
                      const minutesValue = convertFromDisplayValue(inputValue, formData.default_duration_unit);
                      handleInputChange('default_duration_minutes', minutesValue);
                    }}
                    className="flex-1 border border-gray-300 rounded-md px-3 py-2 text-sm"
                    disabled={!user?.primary_firm_id}
                    required
                  />
                  <select
                    value={formData.default_duration_unit}
                    onChange={(e) => handleInputChange('default_duration_unit', e.target.value)}
                    className="border border-gray-300 rounded-md px-3 py-2 text-sm"
                    disabled={!user?.primary_firm_id}
                  >
                    <option value="minutes">Minutes</option>
                    <option value="hours">Hours</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Buffer After (minutes)</label>
                <input
                  type="number"
                  min="0"
                  max="480"
                  value={formData.buffer_after_minutes}
                  onChange={(e) => handleInputChange('buffer_after_minutes', parseInt(e.target.value) || 0)}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                  disabled={!user?.primary_firm_id}
                />
              </div>

              <div>
                <label className="flex items-center mt-6">
                  <input
                    type="checkbox"
                    checked={formData.is_moveable}
                    onChange={(e) => handleInputChange('is_moveable', e.target.checked)}
                    className="rounded border-gray-300 text-yellow-600 focus:ring-yellow-500"
                    disabled={!user?.primary_firm_id}
                  />
                  <span className="ml-2 text-sm text-gray-700">Auto-moveable</span>
                </label>
              </div>
            </div>

            <div className="flex justify-end space-x-2 mt-4">
              <button
                onClick={() => setShowCreateForm(false)}
                className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleCreate}
                disabled={!formData.task_type || !user?.primary_firm_id}
                className="px-4 py-2 bg-yellow-500 text-white rounded-md text-sm font-medium hover:bg-yellow-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
              >
                <Save className="h-4 w-4 mr-1" />
                Create
              </button>
            </div>
          </div>
        )}

        <div className="space-y-6">
          {/* User Defaults */}
          {user?.primary_firm_id && (
            <div>
              <h4 className="text-md font-medium text-gray-900 mb-3 flex items-center">
                <span className="text-lg mr-2">👤</span>
                Personal Defaults
                <span className="ml-2 text-xs px-2 py-1 rounded-full bg-gray-100 text-gray-800">
                  {defaults.user.length}
                </span>
              </h4>
              
              {defaults.user.length === 0 ? (
                <p className="text-sm text-gray-500 italic">No personal defaults configured. Use "Add Default" to create your own time estimates.</p>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {defaults.user.map(defaultItem => (
                    <DefaultCard 
                      key={defaultItem.id} 
                      defaultItem={defaultItem} 
                      formatDuration={formatDuration} 
                      formatTaskType={formatTaskType}
                      isEditing={editingId === defaultItem.id}
                      onEdit={setEditingId}
                      onSave={handleUpdate}
                      onCancel={() => setEditingId(null)}
                      onDelete={handleDelete}
                      canEdit={true}
                    />
                  ))}
                </div>
              )}
            </div>
          )}

          {/* System Defaults */}
          <div>
            <h4 className="text-md font-medium text-gray-900 mb-3 flex items-center">
              <span className="text-lg mr-2">⚙️</span>
              System Defaults
              <span className="ml-2 text-xs px-2 py-1 rounded-full bg-gray-100 text-gray-800">
                {defaults.system.length}
              </span>
            </h4>
            
            {defaults.system.length === 0 ? (
              <p className="text-sm text-gray-500 italic">No system defaults available</p>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {defaults.system.map(defaultItem => (
                  <DefaultCard 
                    key={defaultItem.id} 
                    defaultItem={defaultItem} 
                    formatDuration={formatDuration} 
                    formatTaskType={formatTaskType}
                    isEditing={false}
                    onEdit={() => {}}
                    onSave={() => {}}
                    onCancel={() => {}}
                    onDelete={() => {}}
                    canEdit={false}
                  />
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="mt-8 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <h4 className="text-sm font-medium text-yellow-900 mb-2">How Task Defaults Work</h4>
          <div className="text-sm text-yellow-800 space-y-1">
            <p><strong>Personal Defaults:</strong> Your custom time estimates (highest priority)</p>
            <p><strong>Firm Defaults:</strong> Your firm's standard estimates (medium priority)</p>
            <p><strong>System Defaults:</strong> Built-in estimates for legal tasks (lowest priority)</p>
          </div>
        </div>
      </div>

      <ConfirmDialog
        isOpen={showDeleteConfirm}
        onClose={() => {
          setShowDeleteConfirm(false);
          setDefaultToDelete(null);
        }}
        onConfirm={confirmDelete}
        title="Delete Task Type Default"
        message="Are you sure you want to delete this task type default? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        variant="danger"
      />

      <Notification
        isOpen={notification.isOpen}
        onClose={hideNotification}
        title={notification.title}
        message={notification.message}
        variant={notification.variant}
        duration={notification.duration}
      />
    </div>
  );
};

// Individual Default Card Component
const DefaultCard = ({ 
  defaultItem, 
  formatDuration, 
  formatTaskType, 
  isEditing, 
  onEdit, 
  onSave, 
  onCancel, 
  onDelete,
  editData,
  onEditChange,
  canEdit 
}) => {
  const [localEditData, setLocalEditData] = React.useState({
    default_duration_minutes: defaultItem.default_duration_minutes,
    buffer_after_minutes: defaultItem.buffer_after_minutes,
    is_moveable: defaultItem.is_moveable
  });

  const handleSave = () => {
    onSave(defaultItem.id, localEditData);
  };

  const handleCancel = () => {
    setLocalEditData({
      default_duration_minutes: defaultItem.default_duration_minutes,
      buffer_after_minutes: defaultItem.buffer_after_minutes,
      is_moveable: defaultItem.is_moveable
    });
    onCancel();
  };

  if (isEditing) {
    return (
      <div className="border border-blue-300 rounded-lg p-4 bg-gray-50">
        <h5 className="font-medium text-gray-900 mb-3">
          {formatTaskType(defaultItem.task_type)}
        </h5>
        
        <div className="space-y-3 text-sm">
          <div>
            <label className="block text-gray-700 mb-1">Duration (minutes):</label>
            <input
              type="number"
              min="15"
              max="1440"
              value={localEditData.default_duration_minutes}
              onChange={(e) => setLocalEditData(prev => ({
                ...prev,
                default_duration_minutes: parseInt(e.target.value) || 15
              }))}
              className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
            />
          </div>
          
          <div>
            <label className="block text-gray-700 mb-1">Buffer After (minutes):</label>
            <input
              type="number"
              min="0"
              max="480"
              value={localEditData.buffer_after_minutes}
              onChange={(e) => setLocalEditData(prev => ({
                ...prev,
                buffer_after_minutes: parseInt(e.target.value) || 0
              }))}
              className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
            />
          </div>
          
          <div>
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={localEditData.is_moveable}
                onChange={(e) => setLocalEditData(prev => ({
                  ...prev,
                  is_moveable: e.target.checked
                }))}
                className="rounded border-gray-300 text-yellow-600 focus:ring-yellow-500 mr-2"
              />
              <span className="text-gray-700">Auto-moveable</span>
            </label>
          </div>
        </div>
        
        <div className="flex justify-end space-x-2 mt-4">
          <button
            onClick={handleCancel}
            className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            className="px-3 py-1 text-sm bg-yellow-500 text-white rounded hover:bg-yellow-600"
          >
            Save
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="border border-gray-200 rounded-lg p-4">
      <div className="flex justify-between items-start mb-2">
        <h5 className="font-medium text-gray-900">
          {formatTaskType(defaultItem.task_type)}
        </h5>
        {canEdit && (
          <div className="flex space-x-1">
            <button
              onClick={() => onEdit(defaultItem.id)}
              className="text-gray-400 hover:text-yellow-600"
              title="Edit"
            >
              <Edit3 className="h-4 w-4" />
            </button>
            <button
              onClick={() => onDelete(defaultItem.id)}
              className="text-gray-400 hover:text-red-600"
              title="Delete"
            >
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
        )}
      </div>
      
      <div className="space-y-1 text-sm">
        <div className="flex justify-between">
          <span className="text-gray-500">Duration:</span>
          <span className="font-medium">{formatDuration(defaultItem.default_duration_minutes)}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-gray-500">Buffer:</span>
          <span className="font-medium">{defaultItem.buffer_after_minutes || 0}min</span>
        </div>
        <div className="flex justify-between">
          <span className="text-gray-500">Moveable:</span>
          <span className={`font-medium ${defaultItem.is_moveable ? 'text-green-600' : 'text-red-600'}`}>
            {defaultItem.is_moveable ? 'Yes' : 'No'}
          </span>
        </div>
      </div>
      
      <div className="mt-3 pt-2 border-t border-gray-100">
        <span className={`inline-block px-2 py-1 rounded-full text-xs font-medium ${
          defaultItem.user_id ? 'bg-gray-100 text-gray-800' :
          defaultItem.firm_id ? 'bg-green-100 text-green-800' :
          'bg-gray-100 text-gray-800'
        }`}>
          {defaultItem.user_id ? 'personal' : defaultItem.firm_id ? 'firm' : 'system'} level
        </span>
      </div>
    </div>
  );
};

export default TaskTypeDefaultsManager;